import { useRef, useState } from 'react';
import { ErrorModel } from '../components/common/ErrorModel';
import { NavBar } from '../components/layout/NavBar';
import { MainMenu } from '../components/timeentry/MainMenu';
import { useAuth } from '../context/AuthContext';
import { getAlertsMethod } from '../services/apiClient';
import type { AlertData, SelectedDate } from '../types';

/**
 * The render half of `src/App.js`, and the state that went with it that is not auth.
 *
 * App.js owned two unrelated things: the auth bootstrap — now `context/AuthContext` — and
 * this page. What is left here is the four values NavBar and MainMenu pass between each
 * other (`alertData`, `clickedAlert`, `handleSave`, `selectedDate`) plus the app-level
 * error modal.
 *
 * The nested `<body>` (App.js:245) is preserved. It is invalid HTML — React renders a
 * second `<body>` element inside `#root` and logs a nesting warning — but `App.css` is
 * carried byte-identical and its `body` rules therefore apply to this element too.
 * Removing it is a DOM change, and this migration does not make DOM changes; it is listed
 * in the README as a known issue carried over deliberately.
 */
export function TimeEntryPage(): JSX.Element {
  const {
    loggedUserDetails,
    loggedUserGteEmployeeNo,
    operationalUserGteEmployeeNo,
    error,
    clearError,
  } = useAuth();

  const [alertData, setAlertData] = useState<AlertData | null>(null);
  const [clickedAlert, setClickedAlert] = useState(false);
  const [handleSave, setHandleSave] = useState(false);
  const [selectedDate, setSelectedDate] = useState<SelectedDate | null>(null);

  /**
   * `this.lastRequestId` — an instance field, not state, so writing it never re-rendered.
   * A ref is the exact equivalent.
   */
  const lastRequestId = useRef<string | null>(null);
  /** `loggedUserGteEmployeeNo` is read a second later, so read it then, not now. */
  const loggedUserGteEmployeeNoRef = useRef(loggedUserGteEmployeeNo);
  loggedUserGteEmployeeNoRef.current = loggedUserGteEmployeeNo;

  /**
   * App.js:215-233. The one-second delay debounces the burst of `setAlerts` calls that a
   * week change produces (`getProjectList` and every save path calls it); only the last
   * one survives the requestId check and actually fetches.
   *
   * `utils/services.js:getAlerts` wrapped this call in `new Promise(resolve => resolve(…))`
   * and unwrapped `res.data`. The wrapper added a microtask and nothing else, and apiGet
   * already returns the body, so both hops are gone. No `.catch` here either — a failure
   * rejects unhandled, exactly as today.
   */
  const getAlertsData = (operationalUserGteEmployeeNo: number): void => {
    const requestId = `REQUEST-${Date.now()}`;
    lastRequestId.current = requestId;

    setTimeout(() => {
      if (lastRequestId.current !== requestId) {
        return;
      } else {
        void getAlertsMethod(operationalUserGteEmployeeNo, loggedUserGteEmployeeNoRef.current).then(
          (array) => {
            setAlertData(array as AlertData);
          },
        );
      }
    }, 1000);
  };

  const renderMainComponent = (): JSX.Element => {
    if (loggedUserDetails !== null) {
      return (
        <MainMenu
          key="main-menu-key"
          setAlerts={(user) => {
            getAlertsData(user);
          }}
          error={error}
          alertData={alertData}
          loggedUserDetails={loggedUserDetails}
          loggedUserGteEmployeeNo={loggedUserGteEmployeeNo}
          operationalUserGteEmployeeNo={operationalUserGteEmployeeNo}
          handleSaveDraftRequested={handleSave}
          clickedAlert={clickedAlert}
          updateStateOfClickedAlert={(value) => {
            setClickedAlert(value);
          }}
          updateSelectedDate={(date) => setSelectedDate(date)}
        />
      );
    } else {
      // Empty in the original too — the comment inside it ("You are logout! Please login.")
      // is commented out, so this renders an empty Bootstrap alert box.
      return <div className="alert alert-light" role="alert"></div>;
    }
  };

  return (
    <body>
      <NavBar
        key="nav-bar-key"
        alertData={alertData as { alertSummary?: number } | null}
        handleSave={() => setHandleSave(true)}
        updateStateOfClickedAlert={(value) => {
          setClickedAlert(value);
        }}
        selectedDate={selectedDate}
      />
      <main className="">{renderMainComponent()}</main>
      {error.status && <ErrorModel errorResponse={error} hide={clearError} />}
    </body>
  );
}
